import { apiService } from './api';

// TypeScript Interfaces
export interface CartItem {
  id: string;
  orderId: string;
  siteId: string;
  siteUrl: string;
  siteDa: number;
  siteDr: number;
  siteTraffic: number;
  siteCategory?: string | null;
  siteTat?: string | null;
  price: number;
  publisherName: string;
  publisherEmail: string | null;
  contentText?: string | null;
  contentDocUrl?: string | null;
  contentUrl?: string | null;
  contentImageUrl?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CartResponse {
  success: boolean;
  data: {
    projectId: string;
    projectName: string;
    clientId: string;
    clientName: string;
    clientBudget: number;
    projectBudget: number;
    cartItems: CartItem[];
    cartTotal: number;
    cartCount: number;
    monthlySpent?: number;
    monthPlacementSpent?: number;
    monthOrderSpent?: number;
    budgetRemaining: number;
    budgetUsedPercent: number;
  };
}

export interface AddToCartRequest {
  projectId: string;
  clientId: string;
  siteId: string;
  contentText?: string;
  contentDocUrl?: string;
  contentUrl?: string;
  orderDate?: string;
  submissionType?: 'GBS' | 'LI' | 'BOTH';
  price?: number;
}

export interface AddToCartResponse {
  success: boolean;
  message?: string;
  data: {
    orderId: string;
    orderNumber: string;
    siteUrl: string;
    price: number;
    cartCount: number;
    cartTotal: number;
  };
}

export interface RemoveFromCartResponse {
  success: boolean;
  message: string;
  data: {
    cartCount: number;
    cartTotal: number;
  };
}

export interface CheckoutRequest {
  projectId: string;
  orderMonth: number;
  orderYear: number;
  notes?: string;
  planId?: string;
  /** CLIENT/AGENCY only — pay from wallet balance instead of Stripe */
  paymentMethod?: 'wallet';
}

export interface PlacementSummary {
  id: string;
  placementId: string;
  orderId: string;
  siteUrl: string;
  price: number;
  status: string;
  orderDate: string;
}

export interface CheckoutResponse {
  success: boolean;
  message: string;
  data: {
    placementsCreated: number;
    placements: PlacementSummary[];
    totalCost: number;
    paidWithWallet?: boolean;
    walletDebited?: number;
    walletBalance?: number;
  };
}

export interface CreateStripeCheckoutSessionRequest {
  projectId: string;
  orderMonth: number;
  orderYear: number;
  notes?: string;
  planId?: string;
  successUrl: string;
  cancelUrl: string;
  paymentMethodId?: string;
  /** When true, apply available wallet balance and charge Stripe only the remainder. */
  applyWalletCredit?: boolean;
}

export interface CreateStripeCheckoutSessionResponse {
  success: boolean;
  data: {
    paymentAttemptId: string;
    sessionId: string;
    checkoutUrl: string;
    walletApplyAmount?: number;
    stripeAmount?: number;
    cartTotal?: number;
  };
}

export interface FinalizeStripeCheckoutRequest {
  paymentAttemptId: string;
  sessionId: string;
}

export interface StripeCheckoutCard {
  id: string;
  brand: string;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
}

export interface FinalizeStripeCheckoutResponse {
  success: boolean;
  message: string;
  data: {
    placementsCreated: number;
    placements: PlacementSummary[];
    totalCost: number;
    alreadyFinalized?: boolean;
    card?: StripeCheckoutCard | null;
  };
}

export interface CreateOrdersStripeCheckoutSessionRequest {
  orderIds: string[];
  orderMonth: number;
  orderYear: number;
  notes?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface FinalizeOrdersStripeCheckoutRequest {
  paymentAttemptId: string;
  sessionId: string;
}

export interface AllCartItemsResponse {
  success: boolean;
  data: {
    projects: Array<{
      projectId: string;
      projectName: string;
      projectBudget: number;
      clientId: string;
      clientName: string;
      clientBudget: number;
      cartItems: CartItem[];
      cartTotal: number;
      cartCount: number;
    }>;
    totalItems: number;
    totalAmount: number;
  };
}

export interface ErrorResponse {
  success: false;
  error: string;
}

class CartService {
  /**
   * Get all cart items across all projects for current user
   */
  async getAllCartItems(): Promise<AllCartItemsResponse> {
    try {
      const response = await apiService.get<AllCartItemsResponse>('/cart/all/items');
      return response;
    } catch (error: any) {
      console.error('Error fetching all cart items:', error);
      throw error;
    }
  }

  /**
   * Get all cart items for a specific project
   */
  async getCartItems(projectId: string): Promise<CartResponse> {
    try {
      // Cache-bust so the browser never serves a stale (HTTP 304) cart body.
      // The cart changes frequently (add/remove/checkout) and a stale cache
      // keeps the UI out of sync with the DB (e.g. "Add" button never flips to
      // "Added"). A unique query param forces a fresh 200 response every time.
      const response = await apiService.get<CartResponse>(`/cart/${projectId}`, {
        _t: Date.now(),
      });
      return response;
    } catch (error: any) {
      console.error('Error fetching cart items:', error);
      throw error;
    }
  }

  /**
   * Add a site to the cart for a specific project
   */
  async addToCart(data: AddToCartRequest): Promise<AddToCartResponse> {
    try {
      const response = await apiService.post<AddToCartResponse>('/cart', data);
      return response;
    } catch (error: any) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  /**
   * Remove an item from the cart
   */
  async removeFromCart(orderId: string): Promise<RemoveFromCartResponse> {
    try {
      const response = await apiService.delete<RemoveFromCartResponse>(`/cart/${orderId}`);
      return response;
    } catch (error: any) {
      // Already deleted (double-click / race) — treat as success
      if (error?.response?.status === 404) {
        return {
          success: true,
          message: 'Item already removed from cart',
          data: { cartCount: 0, cartTotal: 0 },
        };
      }
      console.error('Error removing from cart:', error);
      throw error;
    }
  }

  async updateCartItem(
    cartItemId: string,
    data: {
      contentDocUrl?: string | null;
      contentText?: string | null;
      contentUrl?: string | null;
      contentImageUrl?: string | null;
      orderDate?: string;
      submissionType?: 'GBS' | 'LI' | 'BOTH';
      price?: number;
    }
  ): Promise<{ success: boolean; message?: string }> {
    try {
      return await apiService.patch<{ success: boolean; message?: string }>(`/cart/items/${cartItemId}`, data);
    } catch (error: any) {
      console.error('Error updating cart item:', error);
      throw error;
    }
  }

  /**
   * Checkout cart - create placements from cart items
   */
  async checkout(data: CheckoutRequest): Promise<CheckoutResponse> {
    try {
      const response = await apiService.post<CheckoutResponse>('/cart/checkout', data);
      return response;
    } catch (error: any) {
      console.error('Error during checkout:', error);
      throw error;
    }
  }

  async createStripeCheckoutSession(
    data: CreateStripeCheckoutSessionRequest
  ): Promise<CreateStripeCheckoutSessionResponse> {
    try {
      const response = await apiService.post<CreateStripeCheckoutSessionResponse>(
        '/payments/stripe/create-checkout-session',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error creating Stripe checkout session:', error);
      throw error;
    }
  }

  async finalizeStripeCheckout(
    data: FinalizeStripeCheckoutRequest
  ): Promise<FinalizeStripeCheckoutResponse> {
    const maxAttempts = 4;
    let lastError: any;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await apiService.post<FinalizeStripeCheckoutResponse>(
          '/payments/finalize',
          data
        );
      } catch (error: any) {
        lastError = error;
        const status = error?.response?.status;
        const retryable =
          error?.response?.data?.retryable === true ||
          status === 409 ||
          !error?.response ||
          error?.message === 'Network Error' ||
          status === 502 ||
          status === 503 ||
          status === 504;
        console.warn(
          `Error finalizing Stripe checkout (attempt ${attempt}/${maxAttempts}):`,
          error?.response?.data?.error || error?.message || error,
        );
        if (!retryable || attempt === maxAttempts) break;
        await new Promise((r) => setTimeout(r, 1200 * attempt));
      }
    }
    throw lastError;
  }

  async createOrdersStripeCheckoutSession(
    data: CreateOrdersStripeCheckoutSessionRequest
  ): Promise<CreateStripeCheckoutSessionResponse> {
    try {
      const response = await apiService.post<CreateStripeCheckoutSessionResponse>(
        '/payments/stripe/create-orders-checkout-session',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error creating Stripe checkout session for orders:', error);
      throw error;
    }
  }

  async finalizeOrdersStripeCheckout(
    data: FinalizeOrdersStripeCheckoutRequest
  ): Promise<FinalizeStripeCheckoutResponse> {
    try {
      const response = await apiService.post<FinalizeStripeCheckoutResponse>(
        '/payments/finalize-orders',
        data
      );
      return response;
    } catch (error: any) {
      console.error('Error finalizing Stripe checkout for orders:', error);
      throw error;
    }
  }

  /**
   * Get cart summary for multiple projects (used in projects list)
   */
  async getCartSummaries(projectIds: string[]): Promise<Record<string, { count: number; total: number }>> {
    // This will be populated from the projects API response
    // which already includes cartCount and cartTotal
    return {};
  }

  /**
   * Clear entire cart for a project (remove all items)
   */
  async clearCart(projectId: string): Promise<void> {
    try {
      // Get all cart items
      const cartResponse = await this.getCartItems(projectId);
      
      // Remove each item
      const removePromises = cartResponse.data.cartItems.map(item => 
        this.removeFromCart(item.id)
      );
      
      await Promise.all(removePromises);
    } catch (error: any) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  }

  /**
   * Check if a site is already in the cart for a project
   */
  async isSiteInCart(projectId: string, siteId: string): Promise<boolean> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartItems.some(item => item.siteId === siteId);
    } catch (error: any) {
      console.error('Error checking if site is in cart:', error);
      return false;
    }
  }

  /**
   * Get cart count for a specific project
   */
  async getCartCount(projectId: string): Promise<number> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartCount;
    } catch (error: any) {
      console.error('Error getting cart count:', error);
      return 0;
    }
  }

  /**
   * Get cart total for a specific project
   */
  async getCartTotal(projectId: string): Promise<number> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      return cartResponse.data.cartTotal;
    } catch (error: any) {
      console.error('Error getting cart total:', error);
      return 0;
    }
  }

  /**
   * Validate budget before adding to cart
   */
  async validateBudget(projectId: string, additionalAmount: number): Promise<{
    valid: boolean;
    warning?: string;
    projectBudget: number;
    currentTotal: number;
    newTotal: number;
    budgetRemaining: number;
  }> {
    try {
      const cartResponse = await this.getCartItems(projectId);
      const currentTotal = cartResponse.data.cartTotal;
      const newTotal = currentTotal + additionalAmount;
      const projectBudget = cartResponse.data.projectBudget;
      const budgetRemaining = projectBudget - newTotal;

      if (newTotal > projectBudget) {
        return {
          valid: false,
          warning: `Adding this item will exceed the project budget by $${(newTotal - projectBudget).toFixed(2)}`,
          projectBudget,
          currentTotal,
          newTotal,
          budgetRemaining
        };
      }

      return {
        valid: true,
        projectBudget,
        currentTotal,
        newTotal,
        budgetRemaining
      };
    } catch (error: any) {
      console.error('Error validating budget:', error);
      throw error;
    }
  }

  async getDocTitles(urls: string[]): Promise<Array<{ url: string; title: string | null }>> {
    const res = await apiService.post<{
      success: boolean;
      error?: string;
      data: Array<{ url: string; title: string | null }>;
    }>('/google-docs/titles', { urls }, { timeout: 30000 });
    if (!res.success) throw new Error(res.error || 'Failed to read document titles');
    return res.data || [];
  }

  async verifyPublicDocLinks(urls: string[]): Promise<Array<{
    url: string;
    googleDoc: boolean;
    public: boolean;
    reason?: string;
  }>> {
    const res = await apiService.post<{
      success: boolean;
      error?: string;
      data: Array<{ url: string; googleDoc: boolean; public: boolean; reason?: string }>;
    }>('/google-docs/verify-public', { urls }, { timeout: 60000 });
    if (!res.success) throw new Error(res.error || 'Failed to verify document links');
    return res.data || [];
  }
}

export const cartService = new CartService();
export default cartService;
